/** ***********
*
* <asset.service.ts v1.0>
*
* Copyright (c) 2023 AIRBUS OPERATIONS
*
* Contributors:
*  GARY Adrien (NG635E8) [adrien.gary.external@airbus.com]
*
* All rights reserved.
**************/

import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Asset, AssetLoadedElementDetails, CreateAssetResponse, ForceStatusPlayload } from '../models/asset';
import { Event } from '../models/event';
import { Tracker } from '../models/tracker';
import { PictureService } from '../models/scope-management.model';
import { selectAssetsById$ } from '../store/selectors/asset.selectors';
import { map, switchMap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Cache, CacheDeltaResponse } from '../models/cache';
import { CHUNK_SIZE, FileDescriptor, ProtectedSignURLResponse, UploadPart } from '../models/picture';
import { AssetLoaded } from '../models/operation';
import { FileService } from './file.service';

export interface CreateAsset {
    name: string;
    familyId: string;
    pn?: string;
    sn?: string;
    notes?: string;
    trackerId?: string;
    msn?: string;
    section?: string;
    side?: string;
    additionalData?: Pick<
        Asset['additionalData'],
        // I3M
        'assignmentZone' | 'characteristics' | 'compatibleProgram' | 'conditionalMaintenance' | 'technicalWorkstation' |
        // ACT
        'airline' | 'contact' | 'immatriculation' | 'leaser'
    >;
}

export type UpdateAsset = Required<Pick<CreateAsset, 'name'>> &
    Pick<CreateAsset, 'pn' | 'notes' | 'sn' | 'trackerId' | 'additionalData'> &
{
    id: string;
    newFamilyId?: string;
};

export interface TrackerLinkError {
    error: {
        message: string;
    },
}

export interface UploadAssets {
    assets: {
        name: Asset['name'];
        parentFamily: string;
        assetFamily: string;
        pn: Asset['pn'];
        sn: Asset['sn'];
        notes: Asset['notes'];
        tracker: string;
        provider: string;
    }[];
}

export interface CreateAssetsByFile {
    UUID: string;
    assetsCount: number;
    provider: string;
}

const httpOptions = {
    headers: new HttpHeaders({
        'Content-Type': 'application/json'
    })
};

@Injectable({
    providedIn: 'root'
})
export class AssetService implements PictureService {

    private _lastRefreshTime: string;

    constructor(
        private http: HttpClient,
        private store: Store,
        private fileService: FileService,
    ) { }

    /**
     * Get an asset by assetId
     */
    public getAsset(assetId: string): Observable<Asset> {

        return this.http.get<Asset>(`${environment.API_ENDPOINT}/assets/${assetId}`);
    }

    /**
     * return an array of asset which user has rights
     */
    public getAssets(delta?: string): Observable<CacheDeltaResponse<Asset>> {
        return this.http.get<CacheDeltaResponse<Asset>>(`${environment.API_ENDPOINT}/assets`);

    }

    // Delta calling
    // /**
    //  * return an array of asset which user has rights
    //  */
    // public getAssets(delta?: string): Observable<CacheDeltaResponse<Asset>> {
    //     // let params = new HttpParams();

    //     // if (delta !== undefined) {
    //     //   params = params.set('delta', delta);
    //     // }
    //     return this.http.get<CacheDeltaResponse<Asset>>(`${environment.API_ENDPOINT}/assets`);

    // }

    /**
     * return the link status
     */
    public getLinkStatus(trackerId: string): Observable<Tracker | TrackerLinkError> {
        return this.http.get<Tracker | TrackerLinkError>(
            `${environment.API_ENDPOINT}/trackers/${trackerId}/link-status`
        );
    }

    /**
     * return the asset history
     */
    public getAssetHistory(familyId: string, assetId: string, startDate: string, endDate: string): Observable<Event[]> {
        const httpParams = new HttpParams()
            .append('startDate', startDate)
            .append('endDate', endDate);
        return this.http.get<Event[]>(`${environment.API_ENDPOINT}/families/${familyId}/assets/${assetId}/history`, { params: httpParams });
    }

    public createAsset(asset: CreateAsset): Observable<CreateAssetResponse> {
        return this.http.post<CreateAssetResponse>(`${environment.API_ENDPOINT}/families/${asset.familyId}/assets`, asset);
    }

    public deleteAsset(assetId: string, familyId: string): Observable<Asset> {
        return this.http.delete<Asset>(`${environment.API_ENDPOINT}/families/${familyId}/assets/${assetId}`);
    }

    public updateAsset(asset: UpdateAsset, oldAsset: Asset): Observable<Asset> {
        return this.http.put<Asset>(
            `${environment.API_ENDPOINT}/families/${oldAsset.family.id}/assets/${oldAsset.id}`,
            JSON.stringify(asset),
            httpOptions
        );
    }

    public associateAssetToTracker(assetId: string, trackerId: string, familyId: string): Observable<Tracker> {
        return this.http.post<Tracker>(
            `${environment.API_ENDPOINT}/association`,
            {
                'assetId': assetId,
                'trackerId': trackerId,
                'familyId': familyId
            },
            httpOptions
        );
    }

    public dissociateAssetToTracker(assetId: string, trackerId: string, familyId: string): Observable<Tracker> {
        return this.http.post<Tracker>(
            `${environment.API_ENDPOINT}/dissociation`,
            {
                'assetId': assetId,
                'trackerId': trackerId,
                'familyId': familyId
            },
            httpOptions
        );
    }

    public getAssetLoadedElements(assetId: string): Observable<AssetLoadedElementDetails> {
        return this.http.get<AssetLoadedElementDetails>(
            `${environment.API_ENDPOINT}/assets/${assetId}/loaded-elements`
        );
    }

    public forceStatus(payload: ForceStatusPlayload, asset: Asset): Observable<Asset> {
        return this.http.put<Asset>(
            `${environment.API_ENDPOINT}/families/${asset.family.id}/assets/${asset.id}/status`, payload
        );
    }

    public getPicture(assetId: string): Observable<{ picture: string }> {
        return this.store.select(selectAssetsById$(assetId)).pipe(
            switchMap((asset) => this.http.get<{ picture: string }>(`${environment.API_ENDPOINT}/families/${asset.familyId}/assets/${assetId}/picture`))
        );
    }

    public createPictureAsset(file: File, assetId: string, familyId: string): Observable<Asset> {
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
        return this.http.post<FileDescriptor>(
            `${environment.API_ENDPOINT}/families/${familyId}/assets/${assetId}/picture`,
            { picture: file.name, pictureType: file.type },
            httpOptions
        ).pipe(
            switchMap(descriptor => {
                const body = {
                    fileId: descriptor.fileId,
                    fileKey: descriptor.fileKey,
                    parts: totalChunks
                };
                return this._generateSignedURLForAssetPicture(body, assetId, familyId).pipe(
                    switchMap((signedUrlRes) => {
                        const chunksURL = signedUrlRes.slice(0, totalChunks).map(item => item.signedUrl);
                        return this.fileService.uploadMultipartFile(file, chunksURL);
                    }),
                    switchMap(uploadPartsArray => this._finalizeAssetPictureUpload(
                        descriptor.fileId,
                        descriptor.fileKey,
                        uploadPartsArray,
                        assetId,
                        familyId
                    )),
                );
            }),
        );
    }

    private _generateSignedURLForAssetPicture(payload: FileDescriptor, assetId: string, familyId: string): Observable<ProtectedSignURLResponse[]> {
        return this.http.post<ProtectedSignURLResponse[]>(
            `${environment.API_ENDPOINT}/families/${familyId}/assets/${assetId}/picture/signed-url`, payload);
    }

    private _finalizeAssetPictureUpload(uploadId: string, fileKey: string, partsArray: UploadPart[], assetId: string, familyId: string): Observable<Asset> {
        partsArray.sort((a, b) => a.PartNumber - b.PartNumber);
        return this.http.post<Asset>(
            `${environment.API_ENDPOINT}/families/${familyId}/assets/${assetId}/picture/finalize-file`,
            {
                fileId: uploadId,
                fileKey: fileKey,
                partsArray
            }
        );
    }

    public deletePictureAsset(assetId: string, familyId: string): Observable<Asset> {
        return this.http.delete<Asset>(`${environment.API_ENDPOINT}/families/${familyId}/assets/${assetId}/picture`);
    }

    public getAssetDetailedLoadedElements(assetId: string): Observable<AssetLoaded> {
        return this.http.get<AssetLoaded>(`${environment.API_ENDPOINT}/assets/${assetId}/loading`);
    }

    public createAssets(allTextLines: UploadAssets['assets']): Observable<CreateAssetsByFile> {
        return this.http.post(`${environment.API_ENDPOINT}/assets/upload/import`, { assets: allTextLines })
            .pipe(map(data => data as CreateAssetsByFile)
            );
    }
}
